/**
 * Outil « Constructeur d'intervalles » (Boîte à outils, 2nde → 1re).
 *
 * Sur la droite des réels, on règle les bornes d'un intervalle (au doigt ou
 * aux compteurs), incluses ou non, finies ou infinies : la notation « [−2 ; 3[ »
 * et l'inégalité « −2 ≤ x < 3 » suivent, et on teste si un nombre appartient
 * à l'intervalle. « Deux intervalles » : intersection et réunion (qui peut
 * avoir deux morceaux). « Distance » : |x − a| ≤ r ⇔ x ∈ [a − r ; a + r].
 * Calculs : `intervals.ts`.
 */

import { useRef, useState, type PointerEvent } from 'react';
import { cn } from '@jeraisonne/ui';
import type { ToolProps } from '../../tool-types';
import { Note, PRIMARY_BUTTON, Segmented, Stepper, ToggleChip } from '../../ui/controls';
import { FIGURE_BG_CLASS } from '../../ui/svg-parts';
import {
  absInterval,
  absOutside,
  contains,
  formatBound,
  inequality,
  intersect,
  isEmpty,
  notation,
  union,
  unionText,
  type Bound,
  type Interval,
} from './intervals';

type Mode = 'one' | 'two' | 'abs';

const W = 320;
const H = 58;
const L = 22;
const R = W - 22;
const MIN = -6;
const MAX = 6;
const Y = 24;
const I_COLOR = 'var(--color-primary-500)';
const J_COLOR = 'var(--color-accent-600)';
/** étiquettes texte : teintes lisibles dans les deux thèmes */
const LABEL_COLOR: Record<string, string> = {
  [I_COLOR]: 'var(--color-primary-600)',
  [J_COLOR]: 'var(--color-accent-700)',
};
const RES_COLOR = 'var(--color-success-600)';

const xOf = (v: number) => L + ((v - MIN) / (MAX - MIN)) * (R - L);
const vOf = (x: number) => MIN + ((x - L) / (R - L)) * (MAX - MIN);
const snapHalf = (v: number) => Math.min(MAX, Math.max(MIN, Math.round(v * 2) / 2));

const CHALLENGES: ReadonlyArray<{ text: string; target: Interval }> = [
  { text: '−2 < x ≤ 5', target: { lo: { v: -2, closed: false }, hi: { v: 5, closed: true } } },
  { text: 'x ≥ 1,5', target: { lo: { v: 1.5, closed: true }, hi: null } },
  { text: 'x < −3', target: { lo: null, hi: { v: -3, closed: false } } },
  { text: '0 ≤ x ≤ 4', target: { lo: { v: 0, closed: true }, hi: { v: 4, closed: true } } },
  { text: '−1 < x < 1', target: { lo: { v: -1, closed: false }, hi: { v: 1, closed: false } } },
];

/** |x − 2|, |x + 3|, |x| */
const distText = (a: number) =>
  a === 0 ? '|x|' : a < 0 ? `|x + ${formatBound(-a)}|` : `|x − ${formatBound(a)}|`;

const sameBound = (a: Bound | null, b: Bound | null) =>
  a === null || b === null ? a === b : a.v === b.v && a.closed === b.closed;
const same = (I: Interval, J: Interval) => sameBound(I.lo, J.lo) && sameBound(I.hi, J.hi);

export function IntervalBuilderTool(_props: ToolProps) {
  const [mode, setMode] = useState<Mode>('one');
  const [I, setI] = useState<Interval>({
    lo: { v: -2, closed: true },
    hi: { v: 3, closed: false },
  });
  const [J, setJ] = useState<Interval>({ lo: { v: 1, closed: false }, hi: { v: 5, closed: true } });
  const [op, setOp] = useState<'inter' | 'union'>('inter');
  const [x, setX] = useState(1.5);
  const [a, setA] = useState(2);
  const [r, setR] = useState(3);
  const [rel, setRel] = useState<'le' | 'lt' | 'ge' | 'gt'>('le');
  const [challenge, setChallenge] = useState<number | null>(null);

  const result = op === 'inter' ? [intersect(I, J)].filter((K) => !isEmpty(K)) : union(I, J);
  const strict = rel === 'lt' || rel === 'gt';
  const inside = rel === 'le' || rel === 'lt';
  const absParts = inside
    ? [absInterval(a, r, strict)].filter((K) => !isEmpty(K))
    : union(...(absOutside(a, r, strict) as [Interval, Interval]));
  const rMax = 6 - Math.abs(a);
  const absExplain = (() => {
    const lo = formatBound(a - r);
    const hi = formatBound(a + r);
    if (r === 0)
      return {
        le: `la distance est nulle : x = ${formatBound(a)}.`,
        lt: 'une distance n’est jamais strictement négative : aucune solution.',
        ge: 'une distance est toujours positive ou nulle : tous les réels conviennent.',
        gt: `la distance est non nulle : tous les réels sauf ${formatBound(a)}.`,
      }[rel];
    return {
      le: `elle est au plus ${formatBound(r)} pour les réels entre ${lo} et ${hi}, bornes comprises.`,
      lt: `elle est strictement inférieure à ${formatBound(r)} pour les réels strictement entre ${lo} et ${hi}.`,
      ge: `elle est au moins ${formatBound(r)} pour les réels qui ne sont pas dans ${notation(absInterval(a, r, true))}.`,
      gt: `elle est strictement supérieure à ${formatBound(r)} pour les réels qui ne sont pas dans ${notation(absInterval(a, r, false))}.`,
    }[rel];
  })();
  const relSym = { le: '≤', lt: '<', ge: '≥', gt: '>' }[rel];
  const solved = challenge !== null && same(I, CHALLENGES[challenge]!.target);

  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Mode"
        value={mode}
        options={[
          { value: 'one', label: 'Un intervalle' },
          { value: 'two', label: 'Deux intervalles' },
          { value: 'abs', label: 'Distance' },
        ]}
        onChange={setMode}
      />

      {mode === 'one' && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <ToggleChip
              on={challenge !== null}
              onClick={() => setChallenge((c) => (c === null ? 0 : null))}
            >
              Défi
            </ToggleChip>
            {challenge !== null && (
              <p className="text-ink-900 min-w-0 flex-1 text-sm font-semibold" aria-live="polite">
                Défi {challenge + 1}/{CHALLENGES.length} · Règle I pour avoir les réels x tels que{' '}
                {CHALLENGES[challenge]!.text}.
              </p>
            )}
          </div>
          <IntervalLine
            name="I"
            interval={I}
            color={I_COLOR}
            onChange={setI}
            marker={challenge === null ? x : null}
          />
          <BoundControls name="I" interval={I} onChange={setI} />
          {challenge !== null ? (
            <section
              className="bg-xp-50 space-y-2 rounded-2xl p-3"
              aria-live="polite"
              data-testid="ib-challenge"
            >
              <p className="text-ink-900 font-display text-lg font-bold">I = {notation(I)}</p>
              {solved ? (
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-success-600 text-sm font-bold">
                    Juste : {CHALLENGES[challenge]!.text} ⇔ x ∈ {notation(I)}
                  </p>
                  {challenge + 1 < CHALLENGES.length && (
                    <button
                      type="button"
                      className={cn(PRIMARY_BUTTON, 'ml-auto')}
                      onClick={() => setChallenge(challenge + 1)}
                    >
                      Défi suivant
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-ink-600 text-sm">
                  Pas encore : vérifie chaque borne, et si elle est incluse ({'≤'}) ou non ({'<'}).
                </p>
              )}
            </section>
          ) : (
            <section
              className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3"
              data-testid="ib-one"
            >
              <p className="text-ink-900 font-display text-2xl font-bold" aria-live="polite">
                I = {notation(I)}
              </p>
              <p className="text-ink-700 text-sm">
                {isEmpty(I)
                  ? 'Aucun réel : l’intervalle est vide.'
                  : !I.lo && !I.hi
                    ? 'C’est l’ensemble de tous les réels.'
                    : `C’est l’ensemble des réels x tels que ${inequality(I)}.`}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <div className="min-w-0 flex-1">
                  <Stepper
                    label="Tester un nombre x"
                    value={x}
                    min={MIN}
                    max={MAX}
                    step={0.5}
                    format={formatBound}
                    onChange={setX}
                  />
                </div>
                <p className="text-ink-900 text-lg font-bold" data-testid="ib-test">
                  {formatBound(x)} {contains(I, x) ? '∈' : '∉'} I
                </p>
              </div>
            </section>
          )}
          <Note live={false}>
            Crochet tourné vers l’intérieur : la borne est incluse (≤). Tourné vers l’extérieur :
            elle est exclue (&lt;). Du côté de l’infini, le crochet est toujours ouvert.
          </Note>
        </>
      )}

      {mode === 'two' && (
        <>
          <IntervalLine name="I" interval={I} color={I_COLOR} onChange={setI} />
          <BoundControls name="I" interval={I} onChange={setI} />
          <IntervalLine name="J" interval={J} color={J_COLOR} onChange={setJ} />
          <BoundControls name="J" interval={J} onChange={setJ} />
          <Segmented
            ariaLabel="Opération"
            value={op}
            options={[
              { value: 'inter', label: 'I ∩ J (et)' },
              { value: 'union', label: 'I ∪ J (ou)' },
            ]}
            onChange={setOp}
          />
          <ResultLine parts={result} color={RES_COLOR} name={op === 'inter' ? 'I ∩ J' : 'I ∪ J'} />
          <section
            className="border-ink-100 space-y-1 rounded-2xl border px-3 py-3"
            aria-live="polite"
            data-testid="ib-two"
          >
            <p className="text-ink-900 font-display text-lg font-bold">
              {op === 'inter' ? 'I ∩ J' : 'I ∪ J'} = {unionText(result)}
            </p>
            <p className="text-ink-700 text-sm">
              {op === 'inter'
                ? result.length === 0
                  ? 'Aucun réel n’est à la fois dans I et dans J : l’intersection est vide.'
                  : 'Les réels qui sont dans I ET dans J : la partie commune.'
                : result.length === 2
                  ? 'Les réels qui sont dans I OU dans J. Il y a un trou entre les deux morceaux : la réunion n’est pas un intervalle.'
                  : 'Les réels qui sont dans I OU dans J (ou les deux).'}
            </p>
          </section>
        </>
      )}

      {mode === 'abs' && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Stepper
              label="Centre a"
              value={a}
              min={-5}
              max={5}
              step={0.5}
              format={formatBound}
              onChange={(v) => {
                setA(v);
                // la droite va de −6 à 6 : a ± r doit y rester
                setR((old) => Math.min(old, 6 - Math.abs(v)));
              }}
            />
            <Stepper
              label="Rayon r"
              value={r}
              min={0}
              max={rMax}
              step={0.5}
              format={formatBound}
              onChange={setR}
            />
          </div>
          <Segmented
            ariaLabel="Relation"
            value={rel}
            options={[
              { value: 'le', label: '≤' },
              { value: 'lt', label: '<' },
              { value: 'ge', label: '≥' },
              { value: 'gt', label: '>' },
            ]}
            onChange={setRel}
          />
          <ResultLine parts={absParts} color={RES_COLOR} name="solutions" center={a} radius={r} />
          <section
            className="border-ink-100 space-y-1 rounded-2xl border px-3 py-3"
            aria-live="polite"
            data-testid="ib-abs"
          >
            <p className="text-ink-900 font-display text-lg font-bold">
              {distText(a)} {relSym} {formatBound(r)} ⇔ x ∈ {unionText(absParts)}
            </p>
            <p className="text-ink-700 text-sm">
              {distText(a)} est la distance entre x et {formatBound(a)} sur la droite : {absExplain}
            </p>
          </section>
        </>
      )}
    </div>
  );
}

/** Crochet « [ » ou « ] » dessiné à l'abscisse x : `dir` = +1 si les becs vont vers la droite. */
function bracketPath(x: number, dir: 1 | -1, y = Y) {
  const d = 6 * dir;
  return `M${x + d},${y - 11} L${x},${y - 11} L${x},${y + 11} L${x + d},${y + 11}`;
}

function Segment({ I, color, y = Y }: { I: Interval; color: string; y?: number }) {
  if (isEmpty(I)) return null;
  const x1 = I.lo ? xOf(I.lo.v) : 4;
  const x2 = I.hi ? xOf(I.hi.v) : W - 4;
  return (
    <g>
      <line x1={x1} x2={x2} y1={y} y2={y} stroke={color} strokeWidth={6} strokeOpacity={0.75} />
      {!I.lo && (
        <path
          d={`M${x1 + 8},${y - 6} L${x1},${y} L${x1 + 8},${y + 6}`}
          fill="none"
          stroke={color}
          strokeWidth={2.5}
        />
      )}
      {!I.hi && (
        <path
          d={`M${x2 - 8},${y - 6} L${x2},${y} L${x2 - 8},${y + 6}`}
          fill="none"
          stroke={color}
          strokeWidth={2.5}
        />
      )}
      {/* borne gauche : [ si incluse (becs vers la droite), ] sinon */}
      {I.lo && (
        <path
          d={bracketPath(x1, I.lo.closed ? 1 : -1, y)}
          fill="none"
          stroke={color}
          strokeWidth={3}
        />
      )}
      {/* borne droite : ] si incluse (becs vers la gauche), [ sinon */}
      {I.hi && (
        <path
          d={bracketPath(x2, I.hi.closed ? -1 : 1, y)}
          fill="none"
          stroke={color}
          strokeWidth={3}
        />
      )}
    </g>
  );
}

function Axis() {
  return (
    <g aria-hidden>
      <line x1={4} x2={W - 4} y1={Y} y2={Y} stroke="var(--color-ink-400)" strokeWidth={1.5} />
      {Array.from({ length: MAX - MIN + 1 }, (_, i) => MIN + i).map((v) => (
        <g key={v}>
          <line x1={xOf(v)} x2={xOf(v)} y1={Y - 4} y2={Y + 4} stroke="var(--color-ink-500)" />
          <text x={xOf(v)} y={Y + 26} textAnchor="middle" fontSize={12} fill="var(--color-ink-600)">
            {formatBound(v)}
          </text>
        </g>
      ))}
    </g>
  );
}

/** Une droite des réels avec un intervalle dont on fait glisser les bornes. */
function IntervalLine({
  name,
  interval,
  color,
  onChange,
  marker = null,
}: {
  name: string;
  interval: Interval;
  color: string;
  onChange: (I: Interval) => void;
  marker?: number | null;
}) {
  const grab = useRef<'lo' | 'hi' | null>(null);
  const at = (e: PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    if (!r.width) return null;
    const px = ((e.clientX - r.left) / r.width) * W;
    return Number.isFinite(px) ? px : null;
  };
  const move = (which: 'lo' | 'hi', px: number) => {
    const v = snapHalf(vOf(px));
    if (which === 'lo' && interval.lo) {
      const hiV = interval.hi ? interval.hi.v : MAX;
      onChange({ ...interval, lo: { ...interval.lo, v: Math.min(v, hiV) } });
    }
    if (which === 'hi' && interval.hi) {
      const loV = interval.lo ? interval.lo.v : MIN;
      onChange({ ...interval, hi: { ...interval.hi, v: Math.max(v, loV) } });
    }
  };
  const onDown = (e: PointerEvent<SVGSVGElement>) => {
    const px = at(e);
    if (px === null) return;
    const dLo = interval.lo ? Math.abs(xOf(interval.lo.v) - px) : Infinity;
    const dHi = interval.hi ? Math.abs(xOf(interval.hi.v) - px) : Infinity;
    // deux bornes confondues : on prend celle du côté où va le doigt
    const which =
      dLo < dHi ? 'lo' : dHi < dLo ? 'hi' : interval.lo && px > xOf(interval.lo.v) ? 'hi' : 'lo';
    if (Math.min(dLo, dHi) > 26) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    grab.current = which;
  };
  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    if (!grab.current) return;
    const px = at(e);
    if (px !== null) move(grab.current, px);
  };
  const onUp = () => {
    grab.current = null;
  };
  return (
    <div className="mx-auto w-full max-w-[26rem]">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={cn(
          'border-ink-100 block w-full touch-pan-y select-none rounded-2xl border',
          FIGURE_BG_CLASS,
        )}
        role="img"
        aria-label={`${name} = ${notation(interval)} sur la droite des réels.`}
        data-testid={`ib-line-${name}`}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        <Axis />
        <Segment I={interval} color={color} />
        {marker !== null && (
          <path
            d={`M${xOf(marker)},${Y - 8} l-6,-10 l12,0 z`}
            fill={
              contains(interval, marker) ? 'var(--color-success-600)' : 'var(--color-error-500)'
            }
          />
        )}
        <text x={16} y={15} fontSize={13} fontWeight={700} fill={LABEL_COLOR[color] ?? color}>
          {name}
        </text>
      </svg>
    </div>
  );
}

/** Droite qui montre un résultat (un ou deux morceaux), sans poignées. */
function ResultLine({
  parts,
  color,
  name,
  center,
  radius,
}: {
  parts: Interval[];
  color: string;
  name: string;
  center?: number;
  radius?: number;
}) {
  return (
    <div className="mx-auto w-full max-w-[26rem]">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={cn(
          'border-ink-100 block w-full select-none rounded-2xl border',
          FIGURE_BG_CLASS,
        )}
        role="img"
        aria-label={`${name} : ${unionText(parts)}.`}
        data-testid="ib-result-line"
      >
        <Axis />
        {parts.map((K, i) => (
          <Segment key={i} I={K} color={color} />
        ))}
        {center !== undefined && radius !== undefined && (
          <g>
            <circle cx={xOf(center)} cy={Y} r={4} fill="var(--color-ink-900)" />
            <text
              x={xOf(center)}
              y={Y - 14}
              textAnchor="middle"
              fontSize={12}
              fontWeight={700}
              fill="var(--color-ink-900)"
            >
              a
            </text>
          </g>
        )}
        <text x={16} y={15} fontSize={12} fontWeight={700} fill={color}>
          {name}
        </text>
      </svg>
    </div>
  );
}

/** Compteurs et interrupteurs des deux bornes d'un intervalle. */
function BoundControls({
  name,
  interval,
  onChange,
}: {
  name: string;
  interval: Interval;
  onChange: (I: Interval) => void;
}) {
  const side = (which: 'lo' | 'hi') => {
    const b = interval[which];
    const inf = which === 'lo' ? '−∞' : '+∞';
    const label = `${name} ${which === 'lo' ? 'gauche' : 'droite'}`;
    const other = interval[which === 'lo' ? 'hi' : 'lo'];
    return (
      <div className="space-y-1" role="group" aria-label={label}>
        {b ? (
          <Stepper
            label={label}
            value={b.v}
            min={which === 'lo' ? MIN : other ? other.v : MIN}
            max={which === 'lo' ? (other ? other.v : MAX) : MAX}
            step={0.5}
            format={formatBound}
            onChange={(v) => onChange({ ...interval, [which]: { ...b, v } })}
          />
        ) : (
          <p className="border-ink-100 text-ink-900 flex min-h-[3.75rem] items-center justify-center rounded-2xl border text-lg font-bold">
            {inf}
          </p>
        )}
        <div className="flex flex-wrap gap-1">
          {b && (
            <ToggleChip
              on={b.closed}
              onClick={() => onChange({ ...interval, [which]: { ...b, closed: !b.closed } })}
            >
              incluse
            </ToggleChip>
          )}
          <ToggleChip
            on={!b}
            onClick={() =>
              onChange({
                ...interval,
                [which]: b
                  ? null
                  : {
                      v:
                        which === 'lo'
                          ? Math.min(-2, other ? other.v : MAX)
                          : Math.max(2, other ? other.v : MIN),
                      closed: true,
                    },
              })
            }
          >
            {inf}
          </ToggleChip>
        </div>
      </div>
    );
  };
  return (
    <div className="grid grid-cols-2 gap-2">
      {side('lo')}
      {side('hi')}
    </div>
  );
}
